import { useState, useEffect } from 'react';
import { showAlert } from '@/components/AppDialog';
import { doc, getDoc } from 'firebase/firestore';
import { saveAdminConfig } from '@/utils/adminConfig';
import { db } from '../firebase';
import { logAdminAction } from '@/utils/audit';
import AdminLayout from '../components/AdminLayout';
import { Save, CheckCircle2, AlertTriangle, Plus, Trash2, Info, Star, Building2 } from 'lucide-react';

/* Admin editor for the Corporate Services catalog — Firestore `corporateServices/current`.
   CorporatePricing.tsx renders this doc live (onSnapshot), so saves reflect instantly
   on connectcorporate.com/corporate/pricing. Shape mirrors CorporatePricing DEFAULT_FEES. */

type NamePrice = { name: string; price: number | null; rec?: boolean; country?: string };
type Addon = { label: string; note: string; price: number | null; mainlandOnly: boolean; category?: string };
type Pkg = { name: string; tag: string; desc: string; inherit: string; features: string[]; priceYr: number; priceMo: number | null; discount: string | null; trial: string | null; govFees: boolean; popular: boolean };
const ADDON_CATS = ['Compliance', 'Visas', 'PRO & government', 'Wills & POA'];
type FeesModel = {
  setupMainland: NamePrice[];
  setupFreezone: NamePrice[];
  echannel: number;
  visaResidence: number;
  addons: Addon[];
  packages: Pkg[];
};

const DEFAULT_FEES: FeesModel = {
  setupMainland: [{ name: 'Dubai (DED)', price: 17850, rec: true, country: 'UAE' }],
  setupFreezone: [{ name: 'Meydan', price: null, rec: true }, { name: 'IFZA', price: null }, { name: 'DMCC', price: null }, { name: 'RAKEZ', price: null }, { name: 'SHAMS', price: null }, { name: 'Ajman', price: null }],
  echannel: 2625,
  visaResidence: 6090,
  addons: [
    { label: 'goAML Registration', note: 'UAE FIU portal · entity + compliance officer', price: 1000, mainlandOnly: false, category: 'Compliance' },
    { label: 'Employment visa', note: 'Entry permit + e-channel', price: 2625, mainlandOnly: false, category: 'Visas' },
    { label: 'Family visa', note: 'New / renewal', price: 5250, mainlandOnly: false, category: 'Visas' },
    { label: "Dependent / Maid's visa", note: 'Excl. deposit', price: 5000, mainlandOnly: false, category: 'Visas' },
    { label: 'Golden visa', note: '10-year · investor/talent', price: null, mainlandOnly: false, category: 'Visas' },
    { label: 'Green visa', note: 'Freelancer / skilled', price: null, mainlandOnly: false, category: 'Visas' },
    { label: 'Document attestation', note: 'MOFA / embassy / notary', price: 525, mainlandOnly: false, category: 'PRO & government' },
    { label: 'NOC & gov letters', note: 'Per NOC', price: 525, mainlandOnly: false, category: 'PRO & government' },
    { label: 'PO box & mail', note: 'Annual', price: 525, mainlandOnly: false, category: 'PRO & government' },
    { label: 'Payroll & WPS', note: 'Mainland only · monthly', price: null, mainlandOnly: true, category: 'PRO & government' },
    { label: 'POA attestation', note: 'Notary / MOFA', price: 368, mainlandOnly: false, category: 'Wills & POA' },
    { label: 'Expat will (DIFC)', note: 'Partner law firm', price: null, mainlandOnly: false, category: 'Wills & POA' },
  ],
  packages: [
    { name: 'Starter', tag: 'For New Business Setup', desc: 'For first-time founders who need the basics to launch with confidence.', inherit: 'Core features:', features: ['DED / Free Zone registration', 'Licence issuance', 'VAT registration'], priceYr: 1100, priceMo: null, discount: null, trial: 'Free Trial 30-Days', govFees: true, popular: false },
    { name: 'Total Compliance', tag: '', desc: 'For entrepreneurs who want to stay 100% compliant with UAE regulations.', inherit: 'Everything in Starter +', features: ['VAT registration + quarterly VAT filings', 'Accounting & bookkeeping dashboard (cash flow, P&L, balance sheet)', 'UAE Corporate Tax (CT) registration & annual return filing (FTA)', 'Includes up to 100 bookkeeping transactions per month', 'Annual financial statements', '1:1 Consultation with Certified Accountant'], priceYr: 14399, priceMo: 1499, discount: '20% OFF', trial: null, govFees: true, popular: false },
    { name: 'Business-in-a-Box™', tag: '', desc: 'For entrepreneurs who want full legal, accounting, and tax compliance, plus a dedicated accountant.', inherit: 'Everything in Total Compliance +', features: ['Includes up to 200 bookkeeping transactions per month', 'Quarterly compliance advisory session', 'Investor-ready reporting (deck financials, data room prep)', 'Priority support'], priceYr: 22999, priceMo: 2399, discount: '20% OFF', trial: null, govFees: true, popular: true },
  ],
};

const clone = (m: FeesModel): FeesModel => JSON.parse(JSON.stringify(m));

// ── small field helpers ─────────────────────────────────────────────────────────
const inputCls = 'w-full border-2 border-gray-200 rounded-lg px-3 py-2 text-sm font-semibold bg-[var(--brand-soft)] text-gray-900 focus:outline-none focus:border-[var(--brand)]';
const lbl = 'text-[11px] font-bold text-gray-400 uppercase tracking-wide block mb-1';

function PriceInput({ value, onChange }: { value: number | null; onChange: (v: number | null) => void }) {
  return (
    <input type="number" min={0} value={value ?? ''} placeholder="On request"
      onChange={e => onChange(e.target.value === '' ? null : Math.max(0, +e.target.value || 0))}
      className={inputCls} />
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
      <div className="h-1.5 bg-gradient-to-r from-[var(--brand)] to-[var(--brand-d)]" />
      <div className="p-5">
        <div className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4">{title}</div>
        {children}
      </div>
    </div>
  );
}

const AddBtn = ({ onClick, label }: { onClick: () => void; label: string }) => (
  <button onClick={onClick} className="flex items-center gap-1.5 text-[var(--brand-d)] hover:text-[var(--brand-d)] font-bold text-xs mt-3">
    <Plus className="w-4 h-4" /> {label}
  </button>
);
const DelBtn = ({ onClick }: { onClick: () => void }) => (
  <button onClick={onClick} className="text-gray-300 hover:text-red-500 transition-colors p-1.5" title="Remove"><Trash2 className="w-4 h-4" /></button>
);
const RecBtn = ({ on, onClick }: { on: boolean; onClick: () => void }) => (
  <button onClick={onClick} title="Recommend to new clients: pre-selected when they start their setup"
    className={`p-2 rounded-lg border-2 transition-colors ${on ? 'border-[var(--brand)] bg-[var(--brand-soft)] text-[var(--brand-d)]' : 'border-gray-200 text-gray-300 hover:border-[var(--brand-line)]'}`}>
    <Star className="w-4 h-4" fill={on ? 'currentColor' : 'none'} />
  </button>
);

// ── Component ────────────────────────────────────────────────────────────────────
export default function AdminCorporatePricing() {
  const [m, setM] = useState<FeesModel>(DEFAULT_FEES);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'corporateServices', 'current'));
        if (snap.exists()) {
          const d = snap.data() as Partial<FeesModel>;
          setM({
            setupMainland: Array.isArray(d.setupMainland) ? d.setupMainland : DEFAULT_FEES.setupMainland,
            setupFreezone: Array.isArray(d.setupFreezone) ? d.setupFreezone : DEFAULT_FEES.setupFreezone,
            echannel: typeof d.echannel === 'number' ? d.echannel : DEFAULT_FEES.echannel,
            visaResidence: typeof d.visaResidence === 'number' ? d.visaResidence : DEFAULT_FEES.visaResidence,
            addons: Array.isArray(d.addons) ? d.addons : DEFAULT_FEES.addons,
            packages: Array.isArray(d.packages) ? d.packages : DEFAULT_FEES.packages,
          });
        }
      } catch (e: any) {
        console.error('Failed to load corporateServices/current:', e);
        if (e?.code === 'permission-denied') setLoadError('Firestore permission denied: showing defaults. (Sign in as a Firebase-authenticated admin to save.)');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const patch = (fn: (draft: FeesModel) => void) => { setM(prev => { const d = clone(prev); fn(d); return d; }); setSavedAt(null); };
  const setRec = (list: 'setupMainland' | 'setupFreezone', i: number) => patch(d => {
    const arr = d[list]; const was = arr[i].rec; arr.forEach(x => { x.rec = false; }); arr[i].rec = !was;
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveAdminConfig('corporateServices/current', { ...m });
      setSavedAt(new Date().toLocaleTimeString());
      logAdminAction('config.update', 'corporateServices/current');
    } catch (e: any) {
      console.error('Save failed:', e);
      showAlert(e?.code === 'permission-denied'
        ? 'Save failed. Permission denied. This editor needs a Firebase-authenticated admin session.'
        : 'Save failed. Check console.');
    } finally { setSaving(false); }
  };
  const handleReset = () => { setM(clone(DEFAULT_FEES)); setSavedAt(null); };

  const LAYOUT = {
    section: 'Pricing',
    title: 'Corporate Pricing',
    icon: Building2,
    subtitle: 'Company setup, visas, PRO & packages: edits go live on connectcorporate.com/corporate/pricing instantly.',
  };

  if (loading) return <AdminLayout {...LAYOUT} loading loadingLabel="Loading corporate pricing…" />;

  return (
    <AdminLayout {...LAYOUT} spaced>
      {loadError && (
        <div className="flex gap-2 items-start bg-red-50 border border-red-300 rounded-xl px-4 py-3 text-xs font-bold text-red-800">
          <AlertTriangle className="w-4 h-4 flex-none stroke-red-500 mt-0.5" /><span>{loadError}</span>
        </div>
      )}
      <div className="flex gap-2 items-start bg-[var(--brand-soft)] border border-[var(--brand-line)] rounded-xl px-4 py-3 text-xs font-bold text-[var(--brand-d)]">
        <Info className="w-4 h-4 flex-none stroke-[var(--brand)] mt-0.5" />
        <span>Leave any price empty to show <b>“On request”</b>. Features: one per line. Changes are live to customers the moment you save.</span>
      </div>

      {/* Fixed fees — everything is quoted in dirhams, so there is no FX rate to set. */}
      <Card title="Fixed fees">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div><label className={lbl}>E-channel registration (AED)</label>
            <input type="number" min={0} value={m.echannel} onChange={e => patch(d => { d.echannel = Math.max(0, +e.target.value || 0); })} className={inputCls} /></div>
          <div><label className={lbl}>Residence visa / each (AED)</label>
            <input type="number" min={0} value={m.visaResidence} onChange={e => patch(d => { d.visaResidence = Math.max(0, +e.target.value || 0); })} className={inputCls} /></div>
        </div>
      </Card>

      {/* Mainland setups */}
      <Card title="Mainland setup jurisdictions">
        <div className="grid grid-cols-[1fr_110px_150px_44px_auto] gap-2 mb-1">
          <div className={lbl}>Jurisdiction</div><div className={lbl}>Country</div><div className={lbl}>Fee (AED)</div><div className={`${lbl} text-center`}>Rec.</div><div />
        </div>
        <div className="space-y-2">
          {m.setupMainland.map((it, i) => (
            <div key={i} className="grid grid-cols-[1fr_110px_150px_44px_auto] gap-2 items-center">
              <input value={it.name} onChange={e => patch(d => { d.setupMainland[i].name = e.target.value; })} className={inputCls} placeholder="Jurisdiction name" />
              <input value={it.country ?? 'UAE'} onChange={e => patch(d => { d.setupMainland[i].country = e.target.value; })} className={inputCls} placeholder="UAE" />
              <PriceInput value={it.price} onChange={v => patch(d => { d.setupMainland[i].price = v; })} />
              <RecBtn on={!!it.rec} onClick={() => setRec('setupMainland', i)} />
              <DelBtn onClick={() => patch(d => { d.setupMainland.splice(i, 1); })} />
            </div>
          ))}
        </div>
        <AddBtn label="Add mainland jurisdiction" onClick={() => patch(d => { d.setupMainland.push({ name: '', price: null, country: 'UAE' }); })} />
        <p className="text-[11px] text-gray-400 font-medium mt-2">Tap the ★ to set the jurisdiction <b>recommended to new clients</b>: it's pre-selected in the setup estimator. Country groups jurisdictions into a dropdown for clients.</p>
      </Card>

      {/* Free zones */}
      <Card title="Free zones">
        <div className="grid grid-cols-[1fr_150px_44px_auto] gap-2 mb-1">
          <div className={lbl}>Free zone</div><div className={lbl}>Fee (AED)</div><div className={`${lbl} text-center`}>Rec.</div><div />
        </div>
        <div className="space-y-2">
          {m.setupFreezone.map((it, i) => (
            <div key={i} className="grid grid-cols-[1fr_150px_44px_auto] gap-2 items-center">
              <input value={it.name} onChange={e => patch(d => { d.setupFreezone[i].name = e.target.value; })} className={inputCls} placeholder="Free zone name" />
              <PriceInput value={it.price} onChange={v => patch(d => { d.setupFreezone[i].price = v; })} />
              <RecBtn on={!!it.rec} onClick={() => setRec('setupFreezone', i)} />
              <DelBtn onClick={() => patch(d => { d.setupFreezone.splice(i, 1); })} />
            </div>
          ))}
        </div>
        <AddBtn label="Add free zone" onClick={() => patch(d => { d.setupFreezone.push({ name: '', price: null }); })} />
      </Card>

      {/* Add-ons */}
      <Card title="Add-on services (compliance, visas, PRO, wills)">
        <p className="text-[11px] text-gray-400 font-medium mb-3 -mt-2">Category groups the service in the client dashboard (Compliance / Visas / PRO &amp; government / Wills &amp; POA). Leave a fee empty to show “On request”.</p>
        <div className="space-y-3">
          {m.addons.map((a, i) => (
            <div key={i} className="grid grid-cols-1 md:grid-cols-[1.1fr_1.1fr_120px_130px_auto_auto] gap-2 md:items-center border-b border-gray-100 pb-3 md:border-0 md:pb-0">
              <input value={a.label} onChange={e => patch(d => { d.addons[i].label = e.target.value; })} className={inputCls} placeholder="Service" />
              <input value={a.note} onChange={e => patch(d => { d.addons[i].note = e.target.value; })} className={inputCls} placeholder="Note" />
              <PriceInput value={a.price} onChange={v => patch(d => { d.addons[i].price = v; })} />
              <select value={a.category ?? 'Visas'} onChange={e => patch(d => { d.addons[i].category = e.target.value; })} className={inputCls}>
                {ADDON_CATS.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <label className="flex items-center gap-1.5 text-xs font-bold text-gray-600 whitespace-nowrap">
                <input type="checkbox" checked={a.mainlandOnly} onChange={e => patch(d => { d.addons[i].mainlandOnly = e.target.checked; })} /> ML only
              </label>
              <DelBtn onClick={() => patch(d => { d.addons.splice(i, 1); })} />
            </div>
          ))}
        </div>
        <AddBtn label="Add service" onClick={() => patch(d => { d.addons.push({ label: '', note: '', price: null, mainlandOnly: false, category: 'Visas' }); })} />
      </Card>

      {/* Packages */}
      <Card title="Compliance packages">
        <div className="space-y-4">
          {m.packages.map((p, i) => (
            <div key={i} className="border-2 border-gray-100 rounded-xl p-4 relative">
              <div className="absolute top-3 right-3"><DelBtn onClick={() => patch(d => { d.packages.splice(i, 1); })} /></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><label className={lbl}>Name</label><input value={p.name} onChange={e => patch(d => { d.packages[i].name = e.target.value; })} className={inputCls} /></div>
                <div><label className={lbl}>Tag (optional)</label><input value={p.tag} onChange={e => patch(d => { d.packages[i].tag = e.target.value; })} className={inputCls} /></div>
              </div>
              <div className="mt-3"><label className={lbl}>Description</label><input value={p.desc} onChange={e => patch(d => { d.packages[i].desc = e.target.value; })} className={inputCls} /></div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
                <div><label className={lbl}>Price / yr (AED)</label><input type="number" min={0} value={p.priceYr} onChange={e => patch(d => { d.packages[i].priceYr = Math.max(0, +e.target.value || 0); })} className={inputCls} /></div>
                <div><label className={lbl}>Price / mo (AED)</label><PriceInput value={p.priceMo} onChange={v => patch(d => { d.packages[i].priceMo = v; })} /></div>
                <div><label className={lbl}>Discount label</label><input value={p.discount ?? ''} onChange={e => patch(d => { d.packages[i].discount = e.target.value || null; })} className={inputCls} placeholder="e.g. 20% OFF" /></div>
                <div><label className={lbl}>Trial label</label><input value={p.trial ?? ''} onChange={e => patch(d => { d.packages[i].trial = e.target.value || null; })} className={inputCls} placeholder="e.g. 30-Day Free Trial" /></div>
              </div>
              <div className="mt-3"><label className={lbl}>“Inherit” line</label><input value={p.inherit} onChange={e => patch(d => { d.packages[i].inherit = e.target.value; })} className={inputCls} placeholder="e.g. Everything in Starter +" /></div>
              <div className="mt-3"><label className={lbl}>Features (one per line)</label>
                <textarea value={p.features.join('\n')} onChange={e => patch(d => { d.packages[i].features = e.target.value.split('\n').map(s => s.trimEnd()).filter(Boolean); })} rows={Math.max(3, p.features.length)} className={`${inputCls} font-normal leading-relaxed`} /></div>
              <div className="flex gap-5 mt-3">
                <label className="flex items-center gap-1.5 text-xs font-bold text-gray-600"><input type="checkbox" checked={p.govFees} onChange={e => patch(d => { d.packages[i].govFees = e.target.checked; })} /> Show “+ government fees”</label>
                <label className="flex items-center gap-1.5 text-xs font-bold text-gray-600"><input type="checkbox" checked={p.popular} onChange={e => patch(d => { d.packages[i].popular = e.target.checked; })} /> Most popular</label>
              </div>
            </div>
          ))}
        </div>
        <AddBtn label="Add package" onClick={() => patch(d => { d.packages.push({ name: 'New package', tag: '', desc: '', inherit: 'Includes:', features: [], priceYr: 0, priceMo: null, discount: null, trial: null, govFees: true, popular: false }); })} />
      </Card>

      {/* Save / Reset */}
      <div className="flex items-center gap-3 flex-wrap pb-8">
        <button onClick={handleSave} disabled={saving} className="flex items-center gap-2 bg-[var(--brand)] hover:bg-[var(--brand-h)] disabled:opacity-60 text-[var(--brand-on)] font-extrabold text-sm px-5 py-2.5 rounded-xl shadow-md transition-all hover:-translate-y-0.5 active:translate-y-0">
          <Save className="w-4 h-4" />{saving ? 'Saving…' : 'Save corporate pricing'}
        </button>
        <button onClick={handleReset} className="border border-gray-200 bg-white text-gray-600 font-bold text-sm px-4 py-2.5 rounded-xl hover:bg-gray-50 transition-colors">Reset to defaults</button>
        {savedAt && <span className="flex items-center gap-1.5 text-green-600 font-bold text-xs"><CheckCircle2 className="w-4 h-4" /> Saved · live to customers as of {savedAt}</span>}
      </div>
    </AdminLayout>
  );
}
